/**
 * Welcome page - First step of onboarding
 */

import {useNavigate} from 'react-router-dom';
import {Button} from '../../components/ui/Button';
import {Container} from '../../components/ui/Container';
import {ProgressBar} from '../../components/onboarding/ProgressBar';
import {useOnboardingStore} from '@voxapilot/shared/stores';
import {ONBOARDING_STEPS} from '@voxapilot/shared/types';
import {ROUTES} from '../../config/constants';

export function WelcomePage() {
  const navigate = useNavigate();
  const {completeStep, skipOnboarding} = useOnboardingStore();

  const handleGetStarted = () => {
    completeStep(ONBOARDING_STEPS.WELCOME);
    navigate(ROUTES.ONBOARDING_PROFILE);
  };

  const handleSkip = () => {
    skipOnboarding();
    navigate(ROUTES.APP);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col">
      <Container className="flex-1 flex flex-col items-center justify-center py-12">
        <div className="max-w-2xl w-full space-y-8">
          {/* Progress */}
          <div className="mb-8">
            <ProgressBar />
          </div>

          {/* Content */}
          <div className="text-center space-y-6">
            <div className="w-20 h-20 bg-gray-900 rounded-2xl mx-auto flex items-center justify-center">
              <span className="text-white font-bold text-3xl">V</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-textPrimary dark:text-gray-100">
              Welcome to VoxaPilot
            </h1>

            <p className="text-xl text-textSecondary dark:text-gray-400 max-w-xl mx-auto">
              Let's get you set up in just a few minutes. We'll help you create your profile, 
              set up your first meeting, and show you around.
            </p>

            {/* Feature highlights */}
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="space-y-2">
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-lg mx-auto flex items-center justify-center">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">P</span>
                </div>
                <h3 className="font-semibold text-textPrimary dark:text-gray-100">Complete Profile</h3>
                <p className="text-sm text-textSecondary dark:text-gray-400">Set up your account in seconds</p>
              </div>
              <div className="space-y-2">
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-lg mx-auto flex items-center justify-center">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">M</span>
                </div>
                <h3 className="font-semibold text-textPrimary dark:text-gray-100">First Meeting</h3>
                <p className="text-sm text-textSecondary dark:text-gray-400">Create your first meeting</p>
              </div>
              <div className="space-y-2">
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-lg mx-auto flex items-center justify-center">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">F</span>
                </div>
                <h3 className="font-semibold text-textPrimary dark:text-gray-100">Explore Features</h3>
                <p className="text-sm text-textSecondary dark:text-gray-400">Learn about key features</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
            <Button variant="primary" size="lg" onClick={handleGetStarted} className="w-full sm:w-auto">
              Get Started
            </Button>
            <Button variant="ghost" size="lg" onClick={handleSkip} className="w-full sm:w-auto">
              Skip for now
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

